<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import { Menu, Constants } from '@fibl/oxs-common';
import { EAV } from '@fibl/zdb-common';

type Ent = EAV.Entity<Menu.Menu>;

export default defineComponent({
	name: 'SearchMenuDropdownBranch',

	props: {
		mode: {
			type: String as PropType<'dropdown' | 'inline'>,
			default: 'dropdown',
		},
		entry: {
			type: Object as PropType<Ent>,
			required: true,
		},
		routeToRequest: Boolean,
		activeMenuId: String,
	},

	computed: {
		children(): Ent[] {
			return this.$globalStore.menuStore
				.getChildren(this.entry.id)
				.slice(0)
				.sort((a, b) => a.data.sorting - b.data.sorting);
		},
		hasChildren(): boolean {
			return this.children.length > 0;
		},
		isOxs() {
			return this.$globalStore.websiteStore.websiteCode === Constants.WEB_OXS;
		},
		isRdb() {
			return this.$globalStore.websiteStore.websiteCode === Constants.WEB_RDB;
		},
		isActive(): boolean {
			return this.entry.id === this.activeMenuId;
		},
		canSelect(): boolean {
			if (!this.routeToRequest) return true;
			if (!this.$globalStore.websiteStore.getOxsCountrySettings.hasRequestProcess)
				return false;
			if (!['agr', 'ins', 'aut', 'con'].includes(this.$globalStore.tokenStore.role))
				return false;
			if (!this.entry.data.availableRequestTypes?.length || !this.entry.data.categoryFilter) {
				let combined = this.$globalStore.menuStore.getCombinedMenuItem(this.entry);
				if (!combined.data.categoryFilter || !combined.data.availableRequestTypes?.length)
					return false;
			}
			return true;
		},
	},

	emits: ['set-active'],

	methods: {
		open() {
			if (this.isOxs) {
				if (
					this.routeToRequest &&
					this.$globalStore.websiteStore.getOxsCountrySettings.hasRequestProcess
				) {
					this.$router.push({
						name: 'OfferSearch',
						params: { menu: this.entry.id },
						query: { mode: 'request' },
					});
				} else if (this.routeToRequest) {
					this.$router.push({ name: 'OfferSearch', params: { menu: this.entry.id } });
				} else {
					this.emitActive();
				}
			}
			if (this.isRdb) {
				this.$emit('set-active', this.entry);
			}
		},
		emitActive() {
			this.$emit('set-active', this.entry);
		},
	},
	data() {
		return {
			activeId: '',
		};
	},
});
</script>

<template>
	<template v-if="mode === 'inline'">
		<el-button
			text
			@click="open"
			type="primary"
			:class="[isActive ? 'active-button' : '']"
			:disabled="!canSelect"
		>
			{{ $filters.toLocale(entry.data.labels) }}
		</el-button>
		<SearchMenuDropdownBranch
			v-for="child in children"
			:key="child.id"
			:entry="child"
			:route-to-request="routeToRequest"
			mode="inline"
			@set-active="$emit('set-active', $event)"
		/>
	</template>
	<template v-else>
		<el-dropdown-item
			v-if="!hasChildren"
			@click="open"
			:disabled="!canSelect"
		>
			{{ $filters.toLocale(entry.data.labels) }}
		</el-dropdown-item>

		<el-dropdown
			v-else
			trigger="hover"
		>
			<el-button
				text
				@click="emitActive"
				:disabled="!canSelect"
			>
				{{ $filters.toLocale(entry.data.labels) }}
				<el-icon style="margin-left: 5px"><ArrowDown /></el-icon>
			</el-button>
			<template #dropdown>
				<el-dropdown-menu>
					<SearchMenuDropdownBranch
						v-for="child in children"
						:key="child.id"
						:entry="child"
						:route-to-request="routeToRequest"
						mode="dropdown"
						@set-active="$emit('set-active', $event)"
					/>
				</el-dropdown-menu>
			</template>
		</el-dropdown>
	</template>
</template>

<style scoped>
.active-button {
	background-color: hsla(160, 100%, 37%, 1) !important;
	color: white !important;
	font-weight: bold;
	border-radius: 6px;
}
</style>
